import { Col, Row } from "antd";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { MdOutlineBadge } from "react-icons/md";
import Button from "shared/components/Button";
import Form from "shared/components/Form";
import Input from "shared/components/Input";
import Label from "shared/components/Label";
import { NAME_SPACES } from "shared/locales/constants";
import { RELATIONSHIP } from "utils/constants";
import { getAgeFromBirthDate } from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/relatedPersonChild";

const CHILD_ID_AGE_THRESHOLD = 18;

const IdentitySideUpload = ({
  fieldKey,
  label,
  values,
  setFieldValue,
  setPercent,
  onFileRemove,
  validTypes,
  addLabel,
}) => (
  <>
    <Col lg={24} xs={24}>
      <div className="subscription-form--group-label">
        <Label label={label} required />
      </div>
    </Col>
    <Col lg={24} xs={24}>
      <div className="subscription-form--custom-upload">
        <Input
          type="file"
          variant="uploader"
          validTypes={validTypes}
          onUpload={(fileList) => {
            setFieldValue(`proof.${fieldKey}`, fileList);
            setPercent({
              ...values,
              proof: { ...values.proof, [fieldKey]: fileList },
            });
          }}
          onFileRemove={onFileRemove}
          fileList={get(values, `proof.${fieldKey}`, [])}
        >
          <Row align="center">
            <Button type="primary--link">{addLabel}</Button>
          </Row>
        </Input>
      </div>
    </Col>
  </>
);

const IdentityDocumentSides = ({
  groupLabel,
  rectoField,
  versoField,
  rectoLabel,
  versoLabel,
  values,
  setFieldValue,
  setPercent,
  onFileRemove,
  validTypes,
  addLabel,
}) => (
  <Row gutter={[16, 16]}>
    <Col lg={24} xs={24}>
      <div className="subscription-form--group-label">
        <Label
          label={groupLabel}
          icon={<MdOutlineBadge size={16} />}
          required
        />
      </div>
    </Col>
    <IdentitySideUpload
      fieldKey={rectoField}
      label={rectoLabel}
      values={values}
      setFieldValue={setFieldValue}
      setPercent={setPercent}
      onFileRemove={onFileRemove}
      validTypes={validTypes}
      addLabel={addLabel}
    />
    <IdentitySideUpload
      fieldKey={versoField}
      label={versoLabel}
      values={values}
      setFieldValue={setFieldValue}
      setPercent={setPercent}
      onFileRemove={onFileRemove}
      validTypes={validTypes}
      addLabel={addLabel}
    />
  </Row>
);

const Id = ({
  values,
  onFileRemove,
  setFieldValue,
  setPercent,
  subscription,
  paymentConditions = {},
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const PROOF = SUBSCRIPTION.FORM.PROOF;

  const { requiresPayerCin = false } = paymentConditions;

  const relatedPersons = get(
    subscription,
    "project.contact.relatedPersons",
    [],
  ).filter(Boolean);

  const spouse = relatedPersons.find(
    (person) => person?.relationship === RELATIONSHIP.SPOUSE,
  );

  const adultChildren = relatedPersons.filter(
    (person) =>
      person?.relationship === RELATIONSHIP.CHILD &&
      getAgeFromBirthDate(get(person, "identity.user.birthDate")) >=
        CHILD_ID_AGE_THRESHOLD,
  );

  const spouseName = spouse
    ? [
        get(spouse, "identity.user.firstname", ""),
        get(spouse, "identity.user.lastname", ""),
      ]
        .filter(Boolean)
        .join(" ")
    : "";

  const uploadProps = {
    values,
    setFieldValue,
    setPercent,
    onFileRemove,
    validTypes: PROOF.ALLOWED_FILES_TYPE,
    addLabel: PROOF.ACTION.ADD,
  };

  return (
    <div className="subscription-form--wrapper">
      <Form type="vertical" title={PROOF.ID.TITLE} subtitle={PROOF.ID.SUBTITLE}>
        <IdentityDocumentSides
          groupLabel={PROOF.ADHERENT}
          rectoField="cinRecto"
          versoField="cinVerso"
          rectoLabel={PROOF.CIN_RECTO?.LABEL}
          versoLabel={PROOF.CIN_VERSO?.LABEL}
          {...uploadProps}
        />

        {spouse && (
          <IdentityDocumentSides
            groupLabel={spouseName || PROOF.SPOUSE?.LABEL || PROOF.SPOUSE}
            rectoField="spouseCinRecto"
            versoField="spouseCinVerso"
            rectoLabel={PROOF.CIN_RECTO?.LABEL}
            versoLabel={PROOF.CIN_VERSO?.LABEL}
            {...uploadProps}
          />
        )}

        {requiresPayerCin && (
          <IdentityDocumentSides
            groupLabel={PROOF.PAYER_CIN?.LABEL}
            rectoField="payerCinRecto"
            versoField="payerCinVerso"
            rectoLabel={PROOF.PAYER_CIN_RECTO?.LABEL}
            versoLabel={PROOF.PAYER_CIN_VERSO?.LABEL}
            {...uploadProps}
          />
        )}

        {adultChildren.map((child, index) => {
          const userId = get(child, "identity.user.id");
          const firstName = get(child, "identity.user.firstname", "");
          const lastName = get(child, "identity.user.lastname", "");
          const childName = [firstName, lastName].filter(Boolean).join(" ");
          const rectoField = userId
            ? `childCinRecto_${userId}`
            : `childCinRecto_${index}`;
          const versoField = userId
            ? `childCinVerso_${userId}`
            : `childCinVerso_${index}`;

          return (
            <IdentityDocumentSides
              key={userId || index}
              groupLabel={childName || `${PROOF.CHILD} ${index + 1}`}
              rectoField={rectoField}
              versoField={versoField}
              rectoLabel={PROOF.CIN_RECTO?.LABEL}
              versoLabel={PROOF.CIN_VERSO?.LABEL}
              {...uploadProps}
            />
          );
        })}
      </Form>
    </div>
  );
};

export default Id;
